import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CompressVisual = () => (
  <div className="bg-page border border-hairline rounded-sm p-4 space-y-3 mb-7" data-testid="outcome-compress-visual">
    <div className="flex items-center gap-3">
      <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-mist w-11 shrink-0">Before</span>
      <span className="h-2 rounded-full bg-ink/70 flex-1" />
      <span className="font-mono text-[10px] text-fog w-11 text-right">47MB</span>
    </div>
    <div className="flex items-center gap-3">
      <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-mist w-11 shrink-0">After</span>
      <span className="h-2 rounded-full bg-azure" style={{ width: "4%" }} />
      <span className="font-mono text-[10px] text-ink w-11 text-right ml-auto">1.9MB</span>
    </div>
    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-mist pt-1">96% smaller — text stays selectable</p>
  </div>
);

const RedactVisual = () => (
  <div className="bg-page border border-hairline rounded-sm p-4 space-y-2.5 mb-7" data-testid="outcome-redact-visual">
    <div className="h-2 rounded-full bg-ink/10 w-11/12" />
    <div className="flex items-center gap-2">
      <span className="h-2 rounded-full bg-ink/10 w-1/4" />
      <span className="h-2 rounded-sm bg-ink w-1/3" />
      <span className="h-2 rounded-full bg-ink/10 w-1/5" />
    </div>
    <div className="h-2 rounded-full bg-ink/10 w-full" />
    <div className="flex items-center gap-2">
      <span className="h-2 rounded-full bg-ink/10 w-1/5" />
      <span className="h-2 rounded-sm bg-ink w-2/5" />
      <span className="h-2 rounded-full bg-ink/10 w-1/4" />
    </div>
    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-mist pt-1">Burned in — not hidden, gone</p>
  </div>
);

const SignVisual = () => (
  <div className="bg-page border border-hairline rounded-sm p-4 mb-7" data-testid="outcome-sign-visual">
    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-mist mb-2">Countersignature</p>
    <span className="block text-3xl text-azure leading-none" style={{ fontFamily: "Caveat, cursive" }}>A. Renard</span>
    <span className="block border-b border-ink/30 w-44 mt-1.5" />
    <p className="font-mono text-[9px] text-mist mt-2">Signed &amp; dated · audit-trailed</p>
  </div>
);

const VISUALS = { compress: CompressVisual, redact: RedactVisual, sign: SignVisual };

const SCENARIOS = [
  {
    title: "The board pack email rejects",
    problem: "A 47MB board pack, due in ten minutes, bounced by every inbox on the list.",
    ran: "Compress to 2MB",
    result: "1.9MB — every word still selectable, every chart intact.",
    engine: "compress",
    testid: "outcome-compress",
  },
  {
    title: "The disclosure that can't leak",
    problem: "800 pages of due-diligence material with names and figures that must never surface.",
    ran: "Redact, drawn over the previews",
    result: "412 permanent blackouts, burned into the file — not hidden, gone.",
    engine: "redact",
    testid: "outcome-redact",
  },
  {
    title: "The countersignature chase",
    problem: "A closing document waiting on one signature, and nobody wants another portal account.",
    ran: "Sign — signature placed on the page",
    result: "Signed, dated, and downloaded. No accounts, no envelopes, no waiting.",
    engine: "sign",
    testid: "outcome-sign",
  },
];

export const Outcomes = () => (
  <section className="py-24 md:py-32 lg:py-40" data-testid="outcomes-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.span
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        transition={{ duration: 1.2, ease: EASE }}
        className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5"
      >
        04 — Problem to result
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.2, ease: EASE }}
        className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl mb-16 md:mb-20 text-ink max-w-2xl"
      >
        What the problem looks like, and what you hand over.
      </motion.h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        {SCENARIOS.map((s, i) => {
          const Visual = VISUALS[s.engine];
          return (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: EASE, delay: i * 0.12 }}
              className="bg-white border border-edge rounded-sm p-6 md:p-7 flex flex-col"
              data-testid={s.testid}
            >
              <p className="font-medium tracking-tight text-xl text-ink mb-6">{s.title}</p>
              {Visual ? <Visual /> : null}
              <div className="space-y-5 flex-1">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-1.5">The problem</p>
                  <p className="text-sm text-fog leading-relaxed">{s.problem}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-1.5">The run</p>
                  <p className="text-sm text-ink leading-relaxed font-medium">{s.ran}</p>
                </div>
                <div className="border-t border-hairline pt-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-azure mb-1.5">The result</p>
                  <p className="text-sm text-ink leading-relaxed">{s.result}</p>
                </div>
              </div>
              <a
                href="#top"
                onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="mt-7 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-azure hover:text-ink transition-colors duration-300"
                data-testid={`${s.testid}-try`}
              >
                Run it free above <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </a>
            </motion.div>
          );
        })}
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mt-6" data-testid="outcomes-note">
        Typical runs — your files are processed in memory and never stored
      </p>
    </div>
  </section>
);
